import { Mesh, Object3D, Vector3 } from 'three';

const PLANE_HANDLE_NAMES = new Set(['XY', 'YZ', 'XZ', 'XYZ']);

const AXIS_HIDE_THRESHOLD = 0.9;
const HIDDEN_HANDLE_SCALE = 1e-10;

type TranslateAxis = 'X' | 'Y' | 'Z';

type TransformControlsGizmoLike = Object3D & {
  isTransformControlsGizmo?: boolean;
  gizmo: Record<string, Object3D>;
  picker: Record<string, Object3D>;
  eye: Vector3;
  showX: boolean;
  showY: boolean;
  showZ: boolean;
};

type TaggedHandle = Object3D & { tag?: string };

type TransformControlsChangeEvent = { value: unknown };

type ChangeDispatcher = {
  addEventListener: (
    type: string,
    listener: (event: TransformControlsChangeEvent) => void
  ) => void;
  removeEventListener: (
    type: string,
    listener: (event: TransformControlsChangeEvent) => void
  ) => void;
};

export type TransformControlsInteractionState = {
  hovered: boolean;
  dragging: boolean;
};

export function removeTransformControlsPlaneHandles(controls: Object3D): void {
  const handles: Object3D[] = [];
  controls.traverse((child) => {
    if (PLANE_HANDLE_NAMES.has(child.name)) {
      handles.push(child);
    }
  });

  for (const handle of handles) {
    handle.parent?.remove(handle);
    if (handle instanceof Mesh) {
      handle.geometry.dispose();
      if (Array.isArray(handle.material)) {
        handle.material.forEach((material) => material.dispose());
      } else {
        handle.material.dispose();
      }
    }
  }
}

export function keepTranslateArrowsOutward(controls: Object3D): () => void {
  const gizmoHelper = controls.children.find(
    (child) =>
      (child as TransformControlsGizmoLike).isTransformControlsGizmo === true
  ) as TransformControlsGizmoLike | undefined;
  if (!gizmoHelper) {
    return () => {};
  }

  const originalUpdateMatrixWorld = gizmoHelper.updateMatrixWorld;

  gizmoHelper.updateMatrixWorld = (force?: boolean) => {
    originalUpdateMatrixWorld(force);

    const axisEyeDot: Record<TranslateAxis, number> = {
      X: gizmoHelper.eye.x,
      Y: gizmoHelper.eye.y,
      Z: gizmoHelper.eye.z
    };
    const axisShown: Record<TranslateAxis, boolean> = {
      X: gizmoHelper.showX,
      Y: gizmoHelper.showY,
      Z: gizmoHelper.showZ
    };

    const handles = [
      ...gizmoHelper.gizmo.translate.children,
      ...gizmoHelper.picker.translate.children
    ];

    for (const handle of handles) {
      const axis = handle.name;
      if (axis === 'X' || axis === 'Y' || axis === 'Z') {
        const { tag } = handle as TaggedHandle;
        if (Math.abs(axisEyeDot[axis]) > AXIS_HIDE_THRESHOLD) {
          handle.scale.set(
            HIDDEN_HANDLE_SCALE,
            HIDDEN_HANDLE_SCALE,
            HIDDEN_HANDLE_SCALE
          );
          handle.visible = false;
        } else if (tag === 'bwd') {
          handle.visible = false;
        } else if (axisEyeDot[axis] < 0) {
          handle.scale.set(
            Math.abs(handle.scale.x),
            Math.abs(handle.scale.y),
            Math.abs(handle.scale.z)
          );
          if (tag === 'fwd') {
            handle.visible = axisShown[axis];
          }
        }
      }
    }

    Object3D.prototype.updateMatrixWorld.call(gizmoHelper, true);
  };

  return () => {
    gizmoHelper.updateMatrixWorld = originalUpdateMatrixWorld;
  };
}

export function observeTransformControlsInteraction(
  controls: Object3D,
  onChange: (state: TransformControlsInteractionState) => void
): () => void {
  const dispatcher = controls as unknown as ChangeDispatcher;
  const state: TransformControlsInteractionState = {
    hovered: false,
    dragging: false
  };

  const handleAxisChanged = (event: TransformControlsChangeEvent) => {
    state.hovered = event.value !== null && event.value !== undefined;
    onChange({ ...state });
  };

  const handleDraggingChanged = (event: TransformControlsChangeEvent) => {
    state.dragging = event.value === true;
    onChange({ ...state });
  };

  dispatcher.addEventListener('axis-changed', handleAxisChanged);
  dispatcher.addEventListener('dragging-changed', handleDraggingChanged);
  return () => {
    dispatcher.removeEventListener('axis-changed', handleAxisChanged);
    dispatcher.removeEventListener('dragging-changed', handleDraggingChanged);
  };
}
